'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, CheckCircle, XCircle, Filter } from 'lucide-react'

// ─── University design tokens ─────────────────────────────────────────────────
const G = '#d4a843'
const IN = '#0ea5e9'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'
const BR = 'rgba(255,255,255,0.08)'
const CA = 'rgba(255,255,255,0.04)'
const SUCCESS = '#4ade80'
const ERROR = '#f87171'

// ─── Types ────────────────────────────────────────────────────────────────────
export interface QuizResultQuestion {
    question: string
    options: string[]
    correct: string
    student_answer: string
    correct_yn: boolean
}

export interface QuizResult {
    id: string
    progress_id: string | null
    user_id: string | null
    class_id: string | null
    category_id: string | null
    lesson_unit: number
    lesson_number: number
    questions: QuizResultQuestion[]
    score_percentage: number
    duration_seconds: number | null
    attempted_at: string
    degree_name?: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function scoreColor(pct: number): string {
    if (pct >= 90) return SUCCESS
    if (pct >= 70) return IN
    if (pct >= 50) return G
    return ERROR
}

function ScorePill({ pct }: { pct: number }) {
    const c = scoreColor(pct)
    return (
        <span style={{
            fontSize: 13, fontWeight: 800,
            color: c, background: `${c}18`,
            padding: '3px 12px', borderRadius: 20,
            display: 'inline-block', minWidth: 52, textAlign: 'center',
        }}>
            {Math.round(pct)}%
        </span>
    )
}

// ─── Single quiz detail row ───────────────────────────────────────────────────
function QuizDetailRow({ result }: { result: QuizResult }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const correct = Array.isArray(result.questions) ? result.questions.filter(q => q.correct_yn).length : 0
    const total = Array.isArray(result.questions) ? result.questions.length : 0
    const durationMin = result.duration_seconds ? Math.round(result.duration_seconds / 60) : null

    return (
        <>
            <motion.tr
                whileHover={{ background: 'rgba(255,255,255,0.03)' }}
                style={{ cursor: 'pointer', transition: 'background 0.1s', borderBottom: `1px solid ${BR}` }}
                onClick={() => setOpen(o => !o)}
            >
                <td style={{ padding: '14px 16px', fontSize: 13, color: TX }}>
                    {new Date(result.attempted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td style={{ padding: '14px 16px', fontSize: 13, color: TX, fontWeight: 600 }}>
                    {result.degree_name || t('university.quizTable.columns.degree', 'Degree')}
                </td>
                <td style={{ padding: '14px 16px', fontSize: 13, color: MU }}>
                    M{result.lesson_unit} · L{result.lesson_number}
                </td>
                <td style={{ padding: '14px 16px' }}>
                    <ScorePill pct={result.score_percentage} />
                </td>
                <td style={{ padding: '14px 16px', color: MU }}>
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </td>
            </motion.tr>

            <AnimatePresence>
                {open && (
                    <tr key="detail">
                        <td colSpan={5} style={{ padding: 0, border: 'none' }}>
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                style={{ overflow: 'hidden' }}
                            >
                                <div style={{
                                    padding: '16px 20px 20px',
                                    background: 'rgba(255,255,255,0.02)',
                                    borderBottom: `1px solid ${BR}`,
                                }}>
                                    <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: MU, marginBottom: 12 }}>
                                        {t('university.quizTable.detail.correctOf', '{{correct}} of {{total}} correct', { correct, total })}
                                        {durationMin !== null && ` · ${t('university.quizTable.detail.duration', '{{minutes}} min', { minutes: durationMin })}`}
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {(result.questions || []).map((q, qi) => (
                                            <div key={qi} style={{
                                                background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: '12px 16px',
                                                border: `1px solid ${q.correct_yn ? 'rgba(74,222,128,0.2)' : 'rgba(248,113,113,0.2)'}`,
                                                borderLeft: `4px solid ${q.correct_yn ? SUCCESS : ERROR}`,
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                                                    <div style={{ color: q.correct_yn ? SUCCESS : ERROR, flexShrink: 0 }}>
                                                        {q.correct_yn ? <CheckCircle size={16} /> : <XCircle size={16} />}
                                                    </div>
                                                    <p style={{ fontSize: 13, fontWeight: 600, color: TX, margin: 0 }}>{q.question}</p>
                                                </div>
                                                <div style={{ display: 'flex', gap: 8, paddingLeft: 24, flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 8, background: 'rgba(74,222,128,0.12)', color: SUCCESS, fontWeight: 600 }}>
                                                        ✓ {q.correct}
                                                    </span>
                                                    {!q.correct_yn && (
                                                        <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 8, background: 'rgba(248,113,113,0.12)', color: ERROR, fontWeight: 600 }}>
                                                            ✗ {q.student_answer || '—'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </td>
                    </tr>
                )}
            </AnimatePresence>
        </>
    )
}

// ─── Main table component ─────────────────────────────────────────────────────
interface QuizResultsTableProps {
    results: QuizResult[]
    degreeNames: string[]
}

export default function QuizResultsTable({ results, degreeNames }: QuizResultsTableProps) {
    const { t } = useTranslation()
    const [filterDegree, setFilterDegree] = useState<string>('all')

    const filtered = filterDegree === 'all' ? results : results.filter(r => r.degree_name === filterDegree)

    const columns = [
        t('university.quizTable.columns.date', 'Date'),
        t('university.quizTable.columns.degree', 'Degree'),
        t('university.quizTable.columns.position', 'Position'),
        t('university.quizTable.columns.score', 'Score'),
        '',
    ]

    return (
        <div>
            {/* Filter bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <Filter size={16} color={MU} />
                <span style={{ fontSize: 13, fontWeight: 600, color: MU }}>{t('university.quizTable.filterLabel', 'Filter:')}</span>
                <select
                    id="uni-quiz-filter"
                    value={filterDegree}
                    onChange={e => setFilterDegree(e.target.value)}
                    style={{
                        fontSize: 13, padding: '6px 12px', borderRadius: 10,
                        border: `1px solid ${BR}`, outline: 'none',
                        background: 'rgba(255,255,255,0.04)', color: TX, cursor: 'pointer',
                    }}
                >
                    <option value="all">{t('university.quizTable.filterAll', 'All Degrees')}</option>
                    {degreeNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
                {filtered.length !== results.length && (
                    <button
                        onClick={() => setFilterDegree('all')}
                        style={{
                            fontSize: 12, color: IN, background: 'rgba(14,165,233,0.1)',
                            border: 'none', borderRadius: 8, padding: '4px 10px',
                            cursor: 'pointer', fontWeight: 600,
                        }}
                    >
                        {t('university.quizTable.clearFilter', 'Clear')}
                    </button>
                )}
            </div>

            {filtered.length === 0 ? (
                <div style={{
                    padding: '40px', textAlign: 'center',
                    background: CA, borderRadius: 16, border: `1px solid ${BR}`,
                }}>
                    <p style={{ color: MU, margin: 0 }}>{t('university.quizTable.empty', 'No quiz results yet. Complete a lesson quiz to see your results here.')}</p>
                </div>
            ) : (
                <div style={{ background: CA, borderRadius: 16, border: `1px solid ${BR}`, overflow: 'hidden', overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: `1px solid ${BR}` }}>
                                {columns.map((h, i) => (
                                    <th key={i} style={{
                                        padding: '12px 16px', textAlign: 'left',
                                        fontSize: 11, fontWeight: 800,
                                        textTransform: 'uppercase', letterSpacing: '0.07em', color: MU,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((result, i) => (
                                <QuizDetailRow key={result.id || i} result={result} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
